import { ReactElement, useState } from 'react';
import { RequestType } from '../../../../core/types';
import {
  useBranchOptions,
  useChannelForm,
  useDescriptionForm,
  usePersonalDataForm,
  useStoreTypeOptions,
  useSubmitRequest,
  useThemeOptions,
} from '../../hooks';
import { isChannel } from '../../types/channel';
import { SubmissionResult } from '../../types/submission';
import { buildSubmitPayload } from '../../utils/buildSubmitPayload';
import { ChannelStep } from '../ChannelStep/ChannelStep';
import { DescriptionStep } from '../DescriptionStep/DescriptionStep';
import { ErrorStep } from '../ErrorStep/ErrorStep';
import { PersonalDataStep } from '../PersonalDataStep/PersonalDataStep';
import { SuccessStep } from '../SuccessStep/SuccessStep';

type Step = 1 | 2 | 3 | 4;

type SupportFlowProps = {
  type: RequestType;
  onNewRequest: () => void;
};

export const SupportFlow = ({ type, onNewRequest }: SupportFlowProps): ReactElement => {
  const [step, setStep] = useState<Step>(1);
  const [submissionResult, setSubmissionResult] = useState<SubmissionResult>();
  const [hasSubmitFailed, setHasSubmitFailed] = useState(false);
  const personalDataForm = usePersonalDataForm();
  const channelForm = useChannelForm();
  const descriptionForm = useDescriptionForm();
  const themeOptions = useThemeOptions(type);
  const branchOptions = useBranchOptions();
  const storeTypeOptions = useStoreTypeOptions();
  const { submit, isSubmitting } = useSubmitRequest();

  const handleSubmit = async (): Promise<void> => {
    if (!isChannel(channelForm.values.channel)) {
      return;
    }

    const payload = buildSubmitPayload({
      type,
      personalData: personalDataForm.values,
      channel: channelForm.values.channel,
      branchId: channelForm.values.branchId,
      themeId: channelForm.values.themeId,
      description: descriptionForm.values.description,
      files: descriptionForm.files.map((uploaded) => uploaded.file),
      storeTypeOptions: storeTypeOptions.options,
    });

    if (!payload.storeType && storeTypeOptions.error) {
      storeTypeOptions.refetch();
    }

    const result = await submit(payload);

    if (result) {
      setSubmissionResult(result);
      setHasSubmitFailed(false);
      setStep(4);
    } else {
      setHasSubmitFailed(true);
    }
  };

  if (step === 4 && submissionResult && isChannel(channelForm.values.channel)) {
    const themeLabel = themeOptions.options.find(
      (option) => option.id === channelForm.values.themeId,
    )?.label;

    return (
      <SuccessStep
        type={type}
        channel={channelForm.values.channel}
        themeLabel={themeLabel}
        description={descriptionForm.values.description}
        ticketId={submissionResult.ticketId}
        confirmationEmail={submissionResult.confirmationEmail}
        onNewRequest={onNewRequest}
      />
    );
  }

  if (step === 3 && hasSubmitFailed) {
    return <ErrorStep type={type} onRetry={handleSubmit} isRetrying={isSubmitting} />;
  }

  if (step === 3 && isChannel(channelForm.values.channel)) {
    const themeLabel = themeOptions.options.find(
      (option) => option.id === channelForm.values.themeId,
    )?.label;

    return (
      <DescriptionStep
        type={type}
        channel={channelForm.values.channel}
        themeLabel={themeLabel}
        onBack={() => setStep(2)}
        onContinue={handleSubmit}
        isSubmitting={isSubmitting}
        isStoreTypeLoading={storeTypeOptions.loading}
        {...descriptionForm}
      />
    );
  }

  if (step === 2) {
    return (
      <ChannelStep
        type={type}
        themeOptions={themeOptions}
        branchOptions={branchOptions}
        onBack={() => setStep(1)}
        onContinue={() => setStep(3)}
        {...channelForm}
      />
    );
  }

  return <PersonalDataStep type={type} onContinue={() => setStep(2)} {...personalDataForm} />;
};
